import type { ComicPanel, ComicPanelCandidate, ComicProject } from './comicTypes'
import { randomUuid } from './randomUuid'

export function syncPanelImageFromSelection(panel: ComicPanel): ComicPanel {
  const selected = panel.candidates.find((c) => c.id === panel.selectedCandidateId)
  return {
    ...panel,
    imageUrl: selected?.imageUrl ?? panel.candidates[0]?.imageUrl ?? null,
    status: panel.candidates.length > 0 ? (panel.status === 'failed' ? 'failed' : 'done') : panel.status,
  }
}

export function normalizePanelCandidates(
  dto: {
    candidates?: { id?: string; imageUrl?: string; creationTaskId?: string | null; createdAt?: string }[]
    selectedCandidateId?: string | null
    imageUrl?: string | null
  },
): Pick<ComicPanel, 'candidates' | 'selectedCandidateId' | 'imageUrl'> {
  const mapped: ComicPanelCandidate[] = (dto.candidates ?? [])
    .filter((c) => c?.imageUrl)
    .map((c) => ({
      id: c.id && String(c.id) ? String(c.id) : randomUuid(),
      imageUrl: String(c.imageUrl),
      creationTaskId: c.creationTaskId ?? null,
      createdAt: c.createdAt ?? new Date().toISOString(),
    }))

  if (mapped.length === 0 && dto.imageUrl) {
    mapped.push({
      id: randomUuid(),
      imageUrl: dto.imageUrl,
      creationTaskId: null,
      createdAt: new Date().toISOString(),
    })
  }

  const selectedCandidateId =
    dto.selectedCandidateId && mapped.some((c) => c.id === dto.selectedCandidateId)
      ? dto.selectedCandidateId
      : mapped[0]?.id ?? null

  const imageUrl = mapped.find((c) => c.id === selectedCandidateId)?.imageUrl ?? null

  return { candidates: mapped, selectedCandidateId, imageUrl }
}

export function appendPanelCandidates(
  project: ComicProject,
  episodeId: string,
  panelId: string,
  imageUrls: string[],
  creationTaskId: string | null,
  maxStored: number,
): ComicProject {
  if (imageUrls.length === 0) return project
  return {
    ...project,
    episodes: project.episodes.map((ep) => {
      if (ep.id !== episodeId) return ep
      return {
        ...ep,
        panels: ep.panels.map((p) => {
          if (p.id !== panelId) return p
          const now = new Date().toISOString()
          const added: ComicPanelCandidate[] = imageUrls.map((url) => ({
            id: randomUuid(),
            imageUrl: url,
            creationTaskId,
            createdAt: now,
          }))
          let candidates = [...p.candidates, ...added]
          if (candidates.length > maxStored) {
            candidates = candidates.slice(candidates.length - maxStored)
          }
          let selectedCandidateId = p.selectedCandidateId
          if (!selectedCandidateId || !candidates.some((c) => c.id === selectedCandidateId)) {
            selectedCandidateId = candidates.at(-1)?.id ?? null
          }
          return syncPanelImageFromSelection({
            ...p,
            candidates,
            selectedCandidateId,
            errorMessage: undefined,
          })
        }),
      }
    }),
  }
}

export function selectPanelCandidate(
  project: ComicProject,
  episodeId: string,
  panelId: string,
  candidateId: string,
): ComicProject {
  return {
    ...project,
    episodes: project.episodes.map((ep) => {
      if (ep.id !== episodeId) return ep
      return {
        ...ep,
        panels: ep.panels.map((p) => {
          if (p.id !== panelId) return p
          if (!p.candidates.some((c) => c.id === candidateId)) return p
          return syncPanelImageFromSelection({ ...p, selectedCandidateId: candidateId })
        }),
      }
    }),
  }
}

export function removePanelCandidate(
  project: ComicProject,
  episodeId: string,
  panelId: string,
  candidateId: string,
): ComicProject {
  return {
    ...project,
    episodes: project.episodes.map((ep) => {
      if (ep.id !== episodeId) return ep
      return {
        ...ep,
        panels: ep.panels.map((p) => {
          if (p.id !== panelId) return p
          const candidates = p.candidates.filter((c) => c.id !== candidateId)
          let selectedCandidateId = p.selectedCandidateId
          if (!candidates.some((c) => c.id === selectedCandidateId)) {
            selectedCandidateId = candidates.at(-1)?.id ?? null
          }
          const next = syncPanelImageFromSelection({ ...p, candidates, selectedCandidateId })
          return {
            ...next,
            status: candidates.length > 0 ? next.status : 'draft',
          }
        }),
      }
    }),
  }
}
